import { Box, Checkbox, Collapse } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import { get } from "lodash";
import { Field, useForm, useFormState } from "react-final-form";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

/**
 * Self-reported experience editor for the borrower sidebar — the same shape the
 * borrower fills out in the loan application's Background & Experience step:
 * pick the categories that apply, then enter counts for those categories only.
 *
 * Deliberately not an AG Grid, despite the repo default. This isn't a data
 * table — it's paired numeric inputs whose visibility depends on the checkbox
 * above them, and a grid would show all six rows at all times (including
 * ground-up rows to a fix-and-flip borrower). The older
 * RffLoanBorrowerExperienceTableChanges grid is the thing this replaces.
 *
 * Drops into any react-final-form whose values are shaped like a borrower, so
 * edits ride that form's existing save affordance rather than needing their own
 * button. Two hosts today:
 *   • the borrower sidebar's Information tab — values ARE the borrower, no
 *     prefix, saved by RffFormChanges → updateBorrower.
 *   • the Loan Info tab's per-borrower Experience card — values are the whole
 *     loan, so `prefix="borrowers[3]"`, saved by the unified save bar.
 *
 * Writes the flat payload the backend reads: the three hasExperienceWith* flags
 * plus `values[row.name] = { less_than_three_years, greater_than_three_years }`.
 *
 * @param {string} [prefix] - Path to the borrower within form values. Omit when
 *   the form's values are the borrower itself.
 * @param {string[]} [categoryFlags] - Restrict to these categories (by flag).
 *   Defaults to all three. The Loan Info tab uses this to keep its existing
 *   product gating — DSCR loans only ever showed income-producing.
 */
export const EXPERIENCE_CATEGORIES = [
  {
    flag: "hasExperienceWithIncomeProducingProperties",
    label: "Income Producing Properties",
    rows: [
      {
        name: "income_producing_properties",
        label: "Income Producing Properties acquired",
      },
    ],
  },
  {
    flag: "hasExperienceWithFixFlips",
    label: "Fix & Flips",
    rows: [
      { name: "ff_sold", label: "Fix and flips completed and sold" },
      {
        name: "ff_retained_as_rental",
        label: "Fix and flips completed and retained as rental",
      },
    ],
  },
  {
    flag: "hasExperienceWithGroundUpConstruction",
    label: "Ground Up Construction",
    rows: [
      { name: "guc_sold", label: "Ground up construction - completed and sold" },
      {
        name: "guc_retained_as_rental",
        label: "Ground up construction - retained as rental",
      },
      {
        name: "general_contractor_projects",
        label: "Rehab completed and sold as GC for 3rd party investor",
      },
    ],
  },
];

const COUNT_KEYS = ["less_than_three_years", "greater_than_three_years"];

const bucketHasCounts = (bucket) =>
  COUNT_KEYS.some((key) => Number(bucket?.[key]) > 0);

const pickCategories = (categoryFlags) =>
  categoryFlags
    ? EXPERIENCE_CATEGORIES.filter((c) => categoryFlags.includes(c.flag))
    : EXPERIENCE_CATEGORIES;

/**
 * Every form path this editor owns, for hosts that need to reason about its
 * fields from the outside (dirty badges, payload building). Same `prefix` /
 * `categoryFlags` arguments the component takes.
 */
export const experienceFieldPaths = (prefix, categoryFlags) => {
  const p = (name) => (prefix ? `${prefix}.${name}` : name);
  return pickCategories(categoryFlags).flatMap((c) => [
    p(c.flag),
    ...c.rows.flatMap((r) => COUNT_KEYS.map((k) => `${p(r.name)}.${k}`)),
  ]);
};

const BorrowerExperienceEditor = ({ prefix, categoryFlags }) => {
  const categories = pickCategories(categoryFlags);
  if (!categories.length) return null;
  return (
    <Flx column g={1.25} sx={{ py: 0.5 }}>
      {categories.map((category) => (
        <ExperienceCategory
          key={category.flag}
          category={category}
          prefix={prefix}
        />
      ))}
    </Flx>
  );
};

/** One checkbox, with its own count rows revealed directly underneath it. */
const ExperienceCategory = ({ category, prefix }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const path = (name) => (prefix ? `${prefix}.${name}` : name);
  const flagPath = path(category.flag);

  // Open when the flag is set OR when counts already exist. Portal syncs and
  // the FCI import populate the buckets without always setting the flag, and a
  // collapsed card would hide those numbers with no way to reach them.
  const hasCounts = category.rows.some((row) =>
    bucketHasCounts(get(values, path(row.name))),
  );
  const checked = get(values, flagPath) === true || hasCounts;

  const handleToggle = (next) => {
    form.change(flagPath, next);
    if (!next) {
      // Zero the counts rather than clearing them. `undefined` is dropped from
      // the JSON body, so a cleared bucket would never reach the server and the
      // old numbers would survive on the deal — and since update_borrower $sets
      // the whole bucket object, explicit zeros are what actually persist
      // "none of this category".
      category.rows.forEach((row) =>
        form.change(path(row.name), {
          less_than_three_years: 0,
          greater_than_three_years: 0,
        }),
      );
    }
  };

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${checked ? theme.palette.primary.main : grey[200]}`,
        background: checked
          ? alpha(theme.palette.primary.main, 0.05)
          : "#ffffff",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "border-color 160ms ease, background 160ms ease",
      })}
    >
      {/* The checkbox is driven by form.change rather than being an RFF input,
          so register the flag explicitly. Hosts that build their save payload
          from `dirtyFields` (the Loan Info tab) only see fields final-form has
          registered — without this, toggling a category would update `values`
          and never reach the server. */}
      <Field name={flagPath} render={() => null} />

      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.75,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => handleToggle(e.target.checked)}
          sx={{ p: 0.25 }}
        />
        <Txt sx={{ fontSize: 12.5, color: grey[700] }}>{category.label}</Txt>
      </Box>

      {/* No unmountOnExit: unmounting unregisters the count inputs, which drops
          them from `dirtyFields` — so the zeros written on uncheck would never
          be saved by a host that submits from dirty state. */}
      <Collapse in={checked}>
        <Box sx={{ px: 1, pb: 1 }}>
          <Box
            sx={{
              background: "#ffffff",
              border: `1px solid ${grey[200]}`,
              borderRadius: "6px",
              p: 1,
            }}
          >
            <Flx column g={1}>
              {/* Column captions render once here rather than over every row. */}
              <Flx ac g={1}>
                <Txt sx={{ flex: 1, fontSize: 10, color: grey[500] }}>
                  How many have you completed?
                </Txt>
                <Flx g={0.75} sx={{ flexShrink: 0 }}>
                  <ColumnCaption>{"< 3 Yrs"}</ColumnCaption>
                  <ColumnCaption>{"> 3 Yrs"}</ColumnCaption>
                </Flx>
              </Flx>

              {category.rows.map((row) => (
                <CountRow key={row.name} row={row} prefix={prefix} />
              ))}
            </Flx>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// One source of truth for the column width so the captions stay lined up with
// the inputs beneath them.
const COUNT_COL_WIDTH = 62;

const ColumnCaption = ({ children }) => (
  <Txt
    sx={{
      width: COUNT_COL_WIDTH,
      flexShrink: 0,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      textAlign: "center",
      color: grey[500],
    }}
  >
    {children}
  </Txt>
);

const CountRow = ({ row, prefix }) => (
  <Flx ac g={1}>
    <Txt sx={{ flex: 1, minWidth: 0, fontSize: 12, color: grey[600] }}>
      {row.label}
    </Txt>
    <Flx g={0.75} sx={{ flexShrink: 0 }}>
      {COUNT_KEYS.map((key) => (
        <CountInput
          key={key}
          name={
            prefix ? `${prefix}.${row.name}.${key}` : `${row.name}.${key}`
          }
        />
      ))}
    </Flx>
  </Flx>
);

const CountInput = ({ name }) => (
  <Field
    name={name}
    // Numbers, not the strings a bare text input would store — these counts feed
    // rtlExperienceScore and the guideline checks downstream.
    parse={(v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    }}
    render={({ input }) => (
      <Box
        component="input"
        type="number"
        min={0}
        inputMode="numeric"
        placeholder="0"
        {...input}
        value={input.value ?? ""}
        sx={(theme) => ({
          width: COUNT_COL_WIDTH,
          flexShrink: 0,
          textAlign: "center",
          fontSize: 12.5,
          fontFamily: "inherit",
          color: grey[800],
          border: `1px solid ${grey[300]}`,
          borderRadius: "6px",
          py: 0.5,
          px: 0.75,
          outline: "none",
          "&:focus": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        })}
      />
    )}
  />
);

export default BorrowerExperienceEditor;
